import { Fragment, lazy, forwardRef } from 'react'
import { string, array, func } from 'prop-types'
import { css } from '@emotion/react'

import { useOnboarding } from '@/context/OnboardingContext'
import { useClientLoadedContext } from '@/context/clientLoaded'
import { isBotEngine } from '@/utils/products'
import { PRODUCT_PAGE_TABS } from '@/constants'
import { MarkdownRender } from '@/components/MarkdownRender'
import { SuspenseWrapper } from '@/components/Suspense/SuspenseWrapper'
import Section from './Section'
import Gallery from './Gallery'

const OnboardingInstruction = lazy(() => import('./OnboardingInstruction'))

const galleryCss = css`
  padding-bottom: 14px;
`

const chatbotLinkCss = css`
  margin-block: 10px;
`

const Description = ({ id, description, screenshots, videoUrl, innerRef }) => {
  const isClientLoaded = useClientLoadedContext()
  const isChatBot = isBotEngine(id) // Temporary workaround for ChatBot

  const {
    isInstalled,
    hasAcceptedConsent,
    requireConsent,
    requireConfiguration,
    isConfigured,
    introduction,
  } = useOnboarding()

  const checkConsent = requireConsent ? hasAcceptedConsent : true
  const checkConfiguration = requireConfiguration ? isConfigured : true

  const canDisplayOnboarding =
    isInstalled && introduction && checkConsent && checkConfiguration

  const canDisplayGallery = screenshots?.length || videoUrl

  return (
    <Section id={PRODUCT_PAGE_TABS.DESCRIPTION} ref={innerRef}>
      {canDisplayOnboarding && (
        <SuspenseWrapper fallback={<Fragment />}>
          <OnboardingInstruction />
        </SuspenseWrapper>
      )}

      {canDisplayGallery && (
        <div css={galleryCss} data-test="app-gallery">
          <Gallery images={screenshots} videoUrl={videoUrl} />
        </div>
      )}

      <div data-test="app-description-wrapper">
        <MarkdownRender content={description} isRawMarkdown={isClientLoaded} />

        {isChatBot && (
          <p css={chatbotLinkCss}>
            See the pricing at{' '}
            <a
              href="http://chatbot.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
            >
              chatbot.com/pricing/
            </a>
          </p>
        )}
      </div>
    </Section>
  )
}

Description.propTypes = {
  description: string,
  screenshots: array,
  widgets: array,
  videoUrl: string,
  innerRef: func,
  id: string,
}

export default forwardRef((props, ref) => (
  <Description innerRef={ref} {...props} />
))
